import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { tap } from "rxjs/operators";

interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
}

@Injectable({
  providedIn: "root",
})
export class ApiCacheService {
  private readonly store = new Map<string, CacheEntry<unknown>>();

  /**
   * When `true` the cache is completely bypassed — every call goes straight
   * to the network and nothing is stored. Flip this in unit/e2e tests via:
   *   service.bypassCache = true;
   */
  bypassCache = false;

  constructor(private _http: HttpClient) {}

  /**
   * Returns a cached GET response if it is still within the TTL window,
   * otherwise fetches fresh data, stores it, and returns the result.
   *
   * @param url      Full request URL
   * @param ttlMs    Time-to-live in milliseconds
   */
  get<T>(url: string, ttlMs: number): Observable<T> {
    if (!this.bypassCache) {
      const entry = this.store.get(url) as CacheEntry<T> | undefined;
      if (entry && Date.now() - entry.fetchedAt < ttlMs) {
        return of(entry.data);
      }
    }
    return this._http
      .get<T>(url)
      .pipe(tap(data => this.store.set(url, { data, fetchedAt: Date.now() })));
  }

  /** Remove a single URL from the cache, forcing the next call to fetch fresh. */
  invalidate(url: string): void {
    this.store.delete(url);
  }

  /** Clear the entire cache. */
  invalidateAll(): void {
    this.store.clear();
  }
}
